import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useCallStore, CallRecord } from '../store/useCallStore';
import { useAuthStore } from '../store/useAuthStore';
import { useToastStore } from '../store/useToastStore';
import { setCallbackStatus, setCallbackNote } from '../api/status';
import { FIELD_LABEL } from '../constants/fieldDefs';

const API_DATE_RE = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/;

function formatApiDate(s: string): string {
  const m = API_DATE_RE.exec(s);
  if (!m) return s;
  return `${m[3]}.${m[2]}.${m[1]} ${m[4]}:${m[5]}:${m[6]}`;
}

function formatIsoDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const p = (n: number, l = 2) => String(n).padStart(l, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

function renderCell(value: unknown): React.ReactNode {
  if (value === null || value === undefined || value === '') return <span className="text-muted">—</span>;
  if (typeof value === 'boolean') return value ? 'Ja' : 'Nein';
  if (Array.isArray(value)) {
    return value.length === 0
      ? <span className="text-muted">—</span>
      : <span className="badge bg-secondary-subtle text-secondary-emphasis">{value.length} Eintr.</span>;
  }
  if (typeof value === 'string' && API_DATE_RE.test(value)) return formatApiDate(value);
  return String(value);
}

function formatAttribution(by: string, at: string): string | null {
  if (!by && !at) return null;
  const parts: string[] = [];
  if (by) parts.push(`von ${by}`);
  if (at) parts.push(formatIsoDate(at));
  return parts.join(' · ');
}

const TH_BASE: React.CSSProperties = {
  fontSize: '0.7rem', letterSpacing: '0.06em', textTransform: 'uppercase',
  cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
};

type SortDir = 'asc' | 'desc';
interface SortState { key: string; dir: SortDir; }

function SortIcon({ active, dir }: { active: boolean; dir: SortDir }) {
  return <span style={{ opacity: active ? 1 : 0.3, marginLeft: 3 }}>{active ? (dir === 'asc' ? '↑' : '↓') : '↕'}</span>;
}

function NoteCell({ call, readOnly }: { call: CallRecord; readOnly?: boolean }) {
  const { updateStatus } = useCallStore();
  const addToast = useToastStore((s) => s.addToast);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(call.callbackNote);

  async function save() {
    setEditing(false);
    if (draft === call.callbackNote) return;
    updateStatus(call.sessionIdentifier, {
      callbackNote: draft,
      callbackAt: new Date().toISOString(),
    });
    try {
      await setCallbackNote(call.sessionIdentifier, draft);
      addToast(draft ? 'Notiz gespeichert.' : 'Notiz gelöscht.');
    } catch {
      updateStatus(call.sessionIdentifier, { callbackNote: call.callbackNote });
      addToast('Fehler beim Speichern der Notiz.', 'danger');
    }
  }

  if (editing) {
    return (
      <input
        type="text"
        className="form-control form-control-sm"
        style={{ minWidth: 160, fontSize: '0.8rem' }}
        value={draft}
        autoFocus
        maxLength={200}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save();
          if (e.key === 'Escape') { setDraft(call.callbackNote); setEditing(false); }
        }}
      />
    );
  }

  if (readOnly) {
    return (
      <span className={`d-inline-block text-truncate ${call.callbackNote ? '' : 'text-muted'}`} style={{ maxWidth: 200, fontSize: '0.82rem' }} title={call.callbackNote}>
        {call.callbackNote || <span style={{ opacity: 0.4 }}>—</span>}
      </span>
    );
  }

  return (
    <span
      className={`d-inline-block text-truncate ${call.callbackNote ? '' : 'text-muted'}`}
      style={{ maxWidth: 200, fontSize: '0.82rem', cursor: 'text' }}
      title={call.callbackNote || 'Notiz hinzufügen'}
      onClick={() => { setDraft(call.callbackNote); setEditing(true); }}
    >
      {call.callbackNote || <span style={{ opacity: 0.4 }}>+ Notiz</span>}
    </span>
  );
}

export default function CallTable() {
  const { calls, filter, updateStatus, loading } = useCallStore();
  const addToast = useToastStore((s) => s.addToast);
  const isAdmin = useAuthStore((s) => s.user?.role === 'admin');
  // Admin in der „Als Unternehmen ansehen"-Sicht: rein lesend — Statusänderungen
  // würden sonst im falschen Realm (dem des Admins) landen.
  const readOnly = isAdmin && !!filter.viewAsCompanyId;
  const [sort, setSort] = useState<SortState>({ key: 'startDateTime', dir: 'desc' });

  // Neu eingetroffene Anrufe kurz animieren (reinsliden + aufleuchten). „Neu" heißt:
  // Session unbekannt UND jünger als der bisher neueste Stand — dadurch animieren
  // weder der erste Load noch Zeitraum-Wechsel noch das Warming-Nachladen älterer
  // Daten, sondern nur echte Neuankömmlinge.
  const [newIds, setNewIds] = useState<Set<string>>(new Set());
  const knownIdsRef = useRef<Set<string>>(new Set());
  const newestTsRef = useRef('');
  const contextRef = useRef('');
  const seededRef = useRef(false);
  const clearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const context = `${filter.period}|${filter.viewAsCompanyId ?? ''}`;
    const maxTs = calls.reduce((m, c) => {
      const t = String(c['startDateTime'] ?? '');
      return t > m ? t : m;
    }, '');

    if (context !== contextRef.current || !seededRef.current) {
      contextRef.current = context;
      seededRef.current = true;
      knownIdsRef.current = new Set(calls.map((c) => c.sessionIdentifier).filter(Boolean));
      newestTsRef.current = maxTs;
      setNewIds((prev) => (prev.size > 0 ? new Set() : prev));
      return;
    }

    const fresh = calls.filter((c) =>
      c.sessionIdentifier &&
      !knownIdsRef.current.has(c.sessionIdentifier) &&
      String(c['startDateTime'] ?? '') > newestTsRef.current,
    );
    for (const c of calls) if (c.sessionIdentifier) knownIdsRef.current.add(c.sessionIdentifier);
    if (maxTs > newestTsRef.current) newestTsRef.current = maxTs;

    if (fresh.length > 0 && fresh.length <= 50) {
      setNewIds(new Set(fresh.map((c) => c.sessionIdentifier)));
      if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
      clearTimerRef.current = setTimeout(() => setNewIds(new Set()), 3000);
    }
  }, [calls, filter.period, filter.viewAsCompanyId]);

  useEffect(() => () => { if (clearTimerRef.current) clearTimeout(clearTimerRef.current); }, []);

  function handleSort(key: string) {
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }
    );
  }

  const processed = useMemo(() => {
    let result = calls;

    if (filter.search.trim()) {
      const q = filter.search.trim().toLowerCase();
      result = result.filter((c) =>
        filter.fields.some((key) => String(c[key] ?? '').toLowerCase().includes(q)) ||
        String(c.callbackNote ?? '').toLowerCase().includes(q)
      );
    }

    return [...result].sort((a, b) => {
      const aVal = String(a[sort.key] ?? '');
      const bVal = String(b[sort.key] ?? '');
      const cmp = aVal.localeCompare(bVal, 'de', { numeric: true });
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [calls, filter.search, filter.fields, sort]);

  async function toggleStatus(call: CallRecord) {
    const next = !call.callbackDone;
    updateStatus(call.sessionIdentifier, {
      callbackDone: next,
      callbackAt: new Date().toISOString(),
    });
    try {
      await setCallbackStatus(call.sessionIdentifier, next);
      addToast(next ? 'Rückruf als erledigt markiert.' : 'Rückruf als ausstehend markiert.');
    } catch {
      updateStatus(call.sessionIdentifier, {
        callbackDone: call.callbackDone,
        callbackBy: call.callbackBy,
        callbackAt: call.callbackAt,
      });
      addToast('Fehler beim Speichern des Status.', 'danger');
    }
  }

  if (!loading && processed.length === 0) {
    return (
      <div className="text-center text-muted py-5">
        <p className="mb-1 fs-5">Keine Anrufe gefunden</p>
        <p className="mb-0 small">
          {filter.search || filter.missedOnly
            ? 'Filter anpassen oder Suche leeren.'
            : 'Zeitraum anpassen oder auf Aktualisieren klicken.'}
        </p>
      </div>
    );
  }

  const columns = filter.fields;

  return (
    <div className="table-responsive">
      <table className="table table-hover align-middle mb-0">
        <thead style={{ backgroundColor: 'var(--bs-tertiary-bg)', color: 'var(--bs-secondary-color)' }}>
          <tr>
            {columns.map((key) => (
              <th
                key={key}
                className="ps-3 text-nowrap"
                style={TH_BASE}
                onClick={() => handleSort(key)}
              >
                {FIELD_LABEL[key] ?? key}
                <SortIcon active={sort.key === key} dir={sort.dir} />
              </th>
            ))}
            <th style={TH_BASE} className="ps-3" onClick={() => handleSort('callbackNote')}>
              Notiz <SortIcon active={sort.key === 'callbackNote'} dir={sort.dir} />
            </th>
            <th style={TH_BASE} className="text-center pe-3" onClick={() => handleSort('callbackDone')}>
              Rückruf <SortIcon active={sort.key === 'callbackDone'} dir={sort.dir} />
            </th>
          </tr>
        </thead>
        <tbody>
          {processed.map((call, i) => {
            const attribution = formatAttribution(call.callbackBy, call.callbackAt);
            const isNew = !!call.sessionIdentifier && newIds.has(call.sessionIdentifier);
            return (
              <tr
                key={call.sessionIdentifier || `row-${i}`}
                className={`${call.callbackDone ? 'table-success' : ''} ${isNew ? 'call-row-new' : ''}`.trim()}
              >
                {columns.map((key) => (
                  <td key={key} className="ps-3 text-nowrap" style={{ fontSize: '0.85rem', maxWidth: 280 }}>
                    <span
                      className="d-inline-block text-truncate"
                      style={{ maxWidth: 260 }}
                      title={typeof call[key] === 'string' ? (call[key] as string) : undefined}
                    >
                      {renderCell(call[key])}
                    </span>
                  </td>
                ))}
                <td className="ps-3">
                  {isAdmin && !readOnly && call.callbackStatuses ? (
                    <div style={{ fontSize: '0.8rem', minWidth: 180 }}>
                      {call.callbackStatuses.length === 0 ? (
                        <span className="text-muted" style={{ opacity: 0.4 }}>—</span>
                      ) : call.callbackStatuses.map((cs, i) => (
                        <div key={i} className={i > 0 ? 'mt-1 pt-1 border-top' : ''}>
                          <span className="badge bg-secondary-subtle text-secondary-emphasis me-1" style={{ fontSize: '0.62rem' }}>
                            {cs.companyName ?? '—'}
                          </span>
                          {cs.note
                            ? <span className="text-truncate d-inline-block" style={{ maxWidth: 180 }} title={cs.note}>{cs.note}</span>
                            : <span className="text-muted" style={{ opacity: 0.4 }}>keine Notiz</span>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <NoteCell call={call} readOnly={readOnly} />
                  )}
                </td>
                <td className="text-center pe-3">
                  {isAdmin && !readOnly && call.callbackStatuses ? (
                    <div style={{ fontSize: '0.8rem', minWidth: 150 }}>
                      {call.callbackStatuses.length === 0 ? (
                        <span className="text-muted" style={{ opacity: 0.4 }}>—</span>
                      ) : call.callbackStatuses.map((cs, i) => (
                        <div key={i} className={`d-flex align-items-baseline gap-1 flex-wrap ${i > 0 ? 'mt-1 pt-1 border-top' : ''}`}>
                          <span className={`badge ${cs.done ? 'bg-success' : 'bg-secondary'}`} style={{ fontSize: '0.65rem', minWidth: 76 }}>
                            {cs.done ? '✓ Erledigt' : '○ Ausstehend'}
                          </span>
                          <span className="text-muted" style={{ fontSize: '0.65rem', lineHeight: 1.3 }}>
                            {cs.companyName ?? '—'}
                            {cs.updatedBy ? ` · ${cs.updatedBy}` : ''}
                            {cs.updatedAt ? ` · ${formatIsoDate(cs.updatedAt).slice(0, 16)}` : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <>
                      <button
                        className={`btn btn-sm ${call.callbackDone ? 'btn-success' : 'btn-outline-secondary'}`}
                        style={{ minWidth: 110, fontSize: '0.8rem' }}
                        title={readOnly ? 'Nur-Lese-Ansicht (Als Unternehmen ansehen)' : call.callbackDone ? 'Als ausstehend markieren' : 'Als erledigt markieren'}
                        disabled={readOnly}
                        onClick={() => !readOnly && toggleStatus(call)}
                      >
                        {call.callbackDone ? '✓ Erledigt' : '○ Ausstehend'}
                      </button>
                      {attribution && (
                        <div className="mt-1" style={{ fontSize: '0.68rem', lineHeight: 1.2, opacity: 0.65 }}>
                          {attribution}
                        </div>
                      )}
                    </>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
